// src/services/equipmentService.js
//
// EQUIPMENT / MACHINE COST TRACKING — NEW MODULE (D-187)
//
// Tracks serialized HVAC equipment (Outdoor Units, Indoor Units, AHUs,
// Split Systems, etc.) SEPARATELY from Item Master. Equipment is never
// added to Item Master — it stays in this dedicated collection to avoid
// polluting the shared consumables catalog with one-off, project-specific
// equipment specs.
//
// GOVERNANCE:
//   - Every mutation creates a history entry (equipment_instance_history) —
//     immutable audit trail, same discipline as inventory_transactions.
//   - Issuing equipment creates a financial_transactions entry
//     (category: "equipment"). Returning it creates a REVERSAL entry
//     (negative amount) — never edits the original entry, per the
//     Constitution's Golden Rule.
//   - Multi-tenant guard on every read/write — same assertSameCompany()
//     pattern already established for Spare Parts.
//   - Non-blocking financial writes — a financial_transactions failure
//     must never block the actual equipment issue/return (same pattern
//     as closeJob()/issueAgainstMR()).

import {
  collection, doc, addDoc, updateDoc, getDoc, getDocs,
  query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const EQUIPMENT_INSTANCES = "equipment_instances";
const EQUIPMENT_HISTORY   = "equipment_instance_history";

/* ==================================================
   MULTI-TENANT GUARD
================================================== */

const assertSameCompany = (docData, company_id) => {
  if (!company_id) {
    throw new Error("company_id required");
  }
  if (docData.company_id !== company_id) {
    throw new Error("Access denied — equipment belongs to a different company");
  }
};

/* ==================================================
   HISTORY LOGGER (internal)
================================================== */

const logHistory = async ({
  company_id, equipment_id, action,
  project_id = "", project_name = "", site_id = "", site_name = "",
  amount = 0, performed_by = "", performed_by_name = "", remarks = "",
}) => {
  await addDoc(collection(db, EQUIPMENT_HISTORY), {
    company_id,
    equipment_id,
    action,
    project_id,
    project_name,
    site_id,
    site_name,
    amount,
    performed_by,
    performed_by_name,
    performed_at: serverTimestamp(),
    remarks,
  });
};

/* ==================================================
   FINANCIAL ENTRY (internal, non-blocking)
================================================== */

const writeFinancialEntry = async ({
  company_id, equipment_id, item_name,
  project_id, project_name, site_id, site_name,
  amount, reference_type, performed_by, performed_by_name,
}) => {
  try {
    await addDoc(collection(db, "financial_transactions"), {
      company_id,
      type:             "expense",
      category:         "equipment",
      amount,
      project_id:       project_id || "",
      project_name:     project_name || "",
      site_id:          site_id || "",
      site_name:        site_name || "",
      equipment_id,
      item_name:        item_name || "",
      reference_type,
      reference_id:     equipment_id,
      status:           "completed",
      created_by:       performed_by || "",
      created_by_name:  performed_by_name || "",
      created_at:       serverTimestamp(),
      updated_at:       serverTimestamp(),
    });
  } catch (err) {
    console.error("equipmentService — financial_transactions write failed (non-blocking):", err);
  }
};

/* ==================================================
   ONBOARD EXISTING EQUIPMENT (Day-1 setup, no request needed)
================================================== */
// Contractor's already-owned equipment, entered directly with its
// original purchase price. No financial_transactions entry here —
// this equipment's cost was already incurred before this system
// existed; it only gets a financial entry once actually ISSUED to a
// specific project (see issueEquipment below).

export const createManualEquipmentInstance = async (data, user) => {
  const company_id = user.company_id;
  if (!company_id) throw new Error("company_id required");
  if (!data.item_name?.trim()) throw new Error("Item name required");
  if (!data.serial_no?.trim()) throw new Error("Serial number required");

  const ref = await addDoc(collection(db, EQUIPMENT_INSTANCES), {
    company_id,
    item_name:            data.item_name.trim(),
    item_code:            data.item_code || "",
    brand:                data.brand || "",
    capacity_tr:          data.capacity_tr || "",
    serial_no:            data.serial_no.trim(),
    purchase_rate:        Number(data.purchase_rate || 0),
    unit:                 "Nos",
    location:             data.location?.trim() || "Main Store",
    source:               "manual_onboarding",
    status:               "in_store",
    current_project_id:   "",
    current_project_name: "",
    current_site_id:      "",
    current_site_name:    "",
    created_at:           serverTimestamp(),
    created_by:           user.uid || user.id || "",
    created_by_name:      user.name || "",
    updated_at:           serverTimestamp(),
  });

  await logHistory({
    company_id,
    equipment_id:      ref.id,
    action:             "onboarded",
    amount:             0,
    performed_by:       user.uid || user.id || "",
    performed_by_name:  user.name || "",
    remarks:            "Existing stock onboarded",
  });

  return { id: ref.id };
};

/* ==================================================
   RECEIVE NEW EQUIPMENT (Stock or Direct Purchase GRN)
================================================== */
// source: "stock_purchase" | "direct_purchase" — passed by the caller
// depending on which PO mode the GRN was raised under. No financial
// entry here either — Direct/Stock Purchase GRN flows already create
// their own vendor-payable financial_transactions entry through the
// existing, unrelated GRN engine (receiveGRN() in inventoryService.js
// handles that for stock-tracked items; direct-purchase POs handle it
// via their own existing flow). This function only registers the
// SERIALIZED EQUIPMENT RECORD — it does not duplicate that payment
// entry. The equipment's cost is recorded again at ISSUE time, tagged
// to the specific project it goes to, which is the number the Costing
// Card actually needs.

export const receiveEquipmentInstance = async (data, user) => {
  const company_id = user.company_id;
  if (!company_id) throw new Error("company_id required");
  if (!data.item_name?.trim()) throw new Error("Item name required");
  if (!data.serial_no?.trim()) throw new Error("Serial number required");
  if (!["stock_purchase", "direct_purchase"].includes(data.source)) {
    throw new Error("source must be stock_purchase or direct_purchase");
  }

  const ref = await addDoc(collection(db, EQUIPMENT_INSTANCES), {
    company_id,
    item_name:            data.item_name.trim(),
    item_code:            data.item_code || "",
    brand:                data.brand || "",
    capacity_tr:          data.capacity_tr || "",
    serial_no:            data.serial_no.trim(),
    purchase_rate:        Number(data.purchase_rate || 0),
    unit:                 "Nos",
    source:               data.source,
    status:               "in_store",
    current_project_id:   "",
    current_project_name: "",
    current_site_id:      "",
    current_site_name:    "",
    created_at:           serverTimestamp(),
    created_by:           user.uid || user.id || "",
    created_by_name:      user.name || "",
    updated_at:           serverTimestamp(),
  });

  await logHistory({
    company_id,
    equipment_id:      ref.id,
    action:             "received",
    amount:             0,
    performed_by:       user.uid || user.id || "",
    performed_by_name:  user.name || "",
    remarks:            `Received via ${data.source.replace("_", " ")}`,
  });

  return { id: ref.id };
};

/* ==================================================
   UPDATE LOCATION (in-store equipment only — simple field edit,
   no financial/status impact. Installed equipment's location is
   determined by its current project — use Issue/Return for that.)
================================================== */

export const updateEquipmentLocation = async ({ equipment_id, location }, user) => {
  if (!equipment_id) throw new Error("equipment_id required");
  if (!location?.trim()) throw new Error("Location required");
  const company_id = user?.company_id;
  if (!company_id) throw new Error("company_id required");

  const ref  = doc(db, EQUIPMENT_INSTANCES, equipment_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Equipment not found");

  const data = snap.data();
  assertSameCompany(data, company_id);

  if (data.status !== "in_store") {
    throw new Error("Only in-store equipment location can be edited directly. This equipment is currently installed — use Return to Store first.");
  }

  await updateDoc(ref, {
    location:   location.trim(),
    updated_at: serverTimestamp(),
  });

  await logHistory({
    company_id, equipment_id, action: "location_updated",
    amount: 0,
    performed_by: user.uid || user.id || "",
    performed_by_name: user.name || "",
    remarks: `Moved to: ${location.trim()}`,
  });

  return { success: true };
};

/* ==================================================
   ISSUE EQUIPMENT TO A PROJECT
================================================== */

export const issueEquipmentToProject = async ({
  equipment_id, project_id, project_name, site_id, site_name,
}, user) => {
  if (!equipment_id) throw new Error("equipment_id required");
  if (!project_id)   throw new Error("project_id required");
  const company_id = user?.company_id;
  if (!company_id) throw new Error("company_id required");

  const ref  = doc(db, EQUIPMENT_INSTANCES, equipment_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Equipment not found");

  const data = snap.data();
  assertSameCompany(data, company_id);

  if (data.status === "installed") {
    throw new Error(
      `This equipment is already installed at ${data.current_project_name || "another project"}. Return it to store first.`
    );
  }
  if (data.status === "scrapped") {
    throw new Error("This equipment has been scrapped and cannot be issued.");
  }

  await updateDoc(ref, {
    status:               "installed",
    current_project_id:   project_id,
    current_project_name: project_name || "",
    current_site_id:      site_id || "",
    current_site_name:    site_name || "",
    updated_at:           serverTimestamp(),
  });

  const performed_by      = user.uid || user.id || "";
  const performed_by_name = user.name || "";
  const amount            = Number(data.purchase_rate || 0);

  await logHistory({
    company_id, equipment_id, action: "issued",
    project_id, project_name, site_id, site_name,
    amount, performed_by, performed_by_name,
  });

  await writeFinancialEntry({
    company_id, equipment_id, item_name: data.item_name,
    project_id, project_name, site_id, site_name,
    amount, reference_type: "equipment_issue",
    performed_by, performed_by_name,
  });

  return { success: true };
};

/* ==================================================
   RETURN EQUIPMENT TO STORE (reversal entry)
================================================== */

export const returnEquipmentToStore = async ({ equipment_id, remarks = "" }, user) => {
  if (!equipment_id) throw new Error("equipment_id required");
  const company_id = user?.company_id;
  if (!company_id) throw new Error("company_id required");

  const ref  = doc(db, EQUIPMENT_INSTANCES, equipment_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Equipment not found");

  const data = snap.data();
  assertSameCompany(data, company_id);

  if (data.status !== "installed") {
    throw new Error("This equipment is not currently installed anywhere.");
  }

  const prevProjectId   = data.current_project_id;
  const prevProjectName = data.current_project_name;
  const prevSiteId      = data.current_site_id;
  const prevSiteName    = data.current_site_name;

  await updateDoc(ref, {
    status:               "in_store",
    current_project_id:   "",
    current_project_name: "",
    current_site_id:      "",
    current_site_name:    "",
    updated_at:           serverTimestamp(),
  });

  const performed_by      = user.uid || user.id || "";
  const performed_by_name = user.name || "";
  const amount            = Number(data.purchase_rate || 0);

  await logHistory({
    company_id, equipment_id, action: "returned",
    project_id: prevProjectId, project_name: prevProjectName,
    site_id: prevSiteId, site_name: prevSiteName,
    amount: -amount, performed_by, performed_by_name, remarks,
  });

  // Reversal — negative amount, same category, tagged to the project it's
  // being REMOVED from. Never edits the original issue entry (Golden Rule).
  await writeFinancialEntry({
    company_id, equipment_id, item_name: data.item_name,
    project_id: prevProjectId, project_name: prevProjectName,
    site_id: prevSiteId, site_name: prevSiteName,
    amount: -amount, reference_type: "equipment_return",
    performed_by, performed_by_name,
  });

  return { success: true };
};

/* ==================================================
   SCRAP EQUIPMENT
================================================== */

export const scrapEquipmentInstance = async ({ equipment_id, remarks = "" }, user) => {
  if (!equipment_id) throw new Error("equipment_id required");
  const company_id = user?.company_id;
  if (!company_id) throw new Error("company_id required");

  const ref  = doc(db, EQUIPMENT_INSTANCES, equipment_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Equipment not found");

  const data = snap.data();
  assertSameCompany(data, company_id);

  if (data.status === "installed") {
    throw new Error("Return this equipment to store before scrapping it.");
  }

  await updateDoc(ref, {
    status:     "scrapped",
    updated_at: serverTimestamp(),
  });

  await logHistory({
    company_id, equipment_id, action: "scrapped",
    amount: 0,
    performed_by: user.uid || user.id || "",
    performed_by_name: user.name || "",
    remarks,
  });

  return { success: true };
};

/* ==================================================
   FETCH HELPERS
================================================== */

export const fetchAllEquipment = async (company_id) => {
  if (!company_id) throw new Error("company_id required");
  const snap = await getDocs(query(
    collection(db, EQUIPMENT_INSTANCES),
    where("company_id", "==", company_id)
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const fetchEquipmentForProject = async (company_id, project_id) => {
  if (!company_id || !project_id) return [];
  const snap = await getDocs(query(
    collection(db, EQUIPMENT_INSTANCES),
    where("company_id",         "==", company_id),
    where("current_project_id", "==", project_id)
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const fetchEquipmentHistory = async (company_id, equipment_id) => {
  if (!company_id || !equipment_id) return [];
  const snap = await getDocs(query(
    collection(db, EQUIPMENT_HISTORY),
    where("company_id",   "==", company_id),
    where("equipment_id", "==", equipment_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.performed_at?.seconds || 0) - (a.performed_at?.seconds || 0));
};